import React, { useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Group, PopoverProps } from '@mantine/core';
import { useElementSize, useMergedRef } from '@mantine/hooks';
import clsx from 'clsx';
import { DatePickerProps } from '@mantine/dates';
import MaskedDateInput from '../MaskedDateInput';
import MaskedTimeInput from '../MaskedTimeInput';
import { useWorkCalendar } from '../../hooks/useWorkCalendar';
import { toLocalDate } from '../../lib/toLocalDate';
import { FieldA11yContext } from '../../context/FieldA11yContext';
import { useFieldA11y } from '../../hooks/useFieldA11y';
import { combine, splitIso } from './combine';

export type IDateTimeFieldProps = {
    value?: string | null;
    onChange?: (next: string | null) => void;
    minDate?: DatePickerProps['minDate'];
    maxDate?: DatePickerProps['maxDate'];
    excludeDate?: DatePickerProps['excludeDate'];
    withSeconds?: boolean;
    invalid?: boolean;
    /** Выходные — по рабочему календарю (`loadWorkCalendar` из `UiProvider`); без него — суббота и воскресенье */
    workCalendar?: boolean;
    testid?: string;
    /** Доступное имя поля даты; поле времени получает «<имя>, время». В `FormGroup` по умолчанию — её подпись. */
    'aria-label'?: string;
    /** id подписи поля даты; поле времени — она же и «время» */
    'aria-labelledby'?: string;
    /** id описания обеих частей; в `FormGroup` к нему добавляются сообщение под полем и пояснение группы */
    'aria-describedby'?: string;
    /** Ссылка на поле даты — фокус поля формы (`setFocus`, ошибка валидации) */
    ref?: React.Ref<HTMLInputElement>;
    /** Крестики очистки в обеих частях. false — для значений, без которых нельзя (границы периода фильтра). */
    clearable?: boolean;
    placeholder?: string;
    disabled?: boolean;
    readOnly?: boolean;
    name?: string;
    popoverProps?: Partial<PopoverProps>;
};

const TIME_WIDTH = 130;
const TIME_WIDTH_SECONDS = 160;
/** Запас на сглаживание и курсор, чтобы последняя цифра не обрезалась */
const TEXT_SLACK = 4;

let measureCanvas: HTMLCanvasElement | undefined;

/**
 * Ширина, при которой в поле целиком виден текст `sample`: отступы поля (в них значок и крестик), рамки и сам текст
 * в шрифте поля. null — измерить нечем (jsdom).
 */
const fieldNeed = (input: HTMLInputElement | null, sample: string): number | null => {
    const ctx = (measureCanvas ??= document.createElement('canvas')).getContext('2d');
    if (!input || !ctx) return null;
    const cs = getComputedStyle(input);
    ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    const px = (v: string) => parseFloat(v) || 0;
    return Math.ceil(
        px(cs.paddingLeft) +
            px(cs.paddingRight) +
            px(cs.borderLeftWidth) +
            px(cs.borderRightWidth) +
            ctx.measureText(sample).width +
            TEXT_SLACK,
    );
};

const DateTimeField: React.FC<IDateTimeFieldProps> = ({
    invalid,
    workCalendar,
    testid,
    excludeDate,
    value,
    onChange,
    minDate,
    maxDate,
    withSeconds = false,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    ref,
    clearable = true,
    placeholder,
    popoverProps,
    disabled,
    readOnly,
    name,
}) => {
    const calendar = useWorkCalendar(!!workCalendar);
    // Связь с группой берёт составное поле целиком: дата — имя группы, время — «<имя>, время».
    const group = useContext(FieldA11yContext);
    const fieldA11y = useFieldA11y({
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        'aria-describedby': ariaDescribedBy,
    });
    const timeWordId = `${useId()}time`;
    const groupLabelText = !ariaLabel && !ariaLabelledBy ? group?.labelText : undefined;
    const timeLabel = ariaLabel ? `${ariaLabel}, время` : groupLabelText ? `${groupLabelText}, время` : undefined;
    // подпись не строка — имя времени собирается из неё и скрытого слова
    const timeLabelledBy =
        !timeLabel && fieldA11y['aria-labelledby'] ? `${fieldA11y['aria-labelledby']} ${timeWordId}` : undefined;
    const isExcluded =
        workCalendar && !excludeDate ? (dateStr: string) => calendar.isHoliday(toLocalDate(dateStr)) : excludeDate;

    const [{ date, time }, setParts] = useState(() => splitIso(value, withSeconds));

    // Сдвоенное поле, пока дата и время видны целиком: время ужимается до своего минимума, а если и так дате
    // не хватает места (в узкой колонке оставались только значок и крестик) — время уходит строкой ниже.
    const timeWidth = withSeconds ? TIME_WIDTH_SECONDS : TIME_WIDTH;
    const { ref: sizeRef, width: rowWidth } = useElementSize<HTMLDivElement>();
    const rowRef = useRef<HTMLDivElement>(null);
    const mergedRowRef = useMergedRef(sizeRef, rowRef);
    const [need, setNeed] = useState<{ date: number; time: number } | null>(null);
    useLayoutEffect(() => {
        const inputs = rowRef.current?.querySelectorAll('input');
        const date = fieldNeed(inputs?.[0] ?? null, '00.00.0000');
        const time = fieldNeed(inputs?.[1] ?? null, withSeconds ? '00:00:00' : '00:00');
        setNeed((prev) =>
            date === null || time === null || (prev?.date === date && prev.time === time) ? prev : { date, time },
        );
    }, [rowWidth, withSeconds]);
    const stacked = !!need && rowWidth > 0 && rowWidth < need.date + need.time;
    const inlineTimeWidth =
        need && rowWidth > 0 ? Math.max(need.time, Math.min(timeWidth, rowWidth - need.date)) : timeWidth;

    useEffect(() => {
        setParts((prev) => {
            // value стал null, потому что очистили ТОЛЬКО время (дата осталась во
            // внутреннем state) — сохраняем дату, не давая round-trip combine→null
            // затереть выбор. Иначе Backspace по времени стирал бы и дату.
            if ((value ?? null) === null && prev.date && !prev.time) {
                return { date: prev.date, time: '' };
            }
            return splitIso(value, withSeconds);
        });
    }, [value, withSeconds]);

    const emit = (nextDate: string | null, nextTime: string) => {
        const next = combine(nextDate, nextTime, withSeconds);
        if (next === (value ?? null)) return;
        onChange?.(next);
    };

    return (
        <Group
            ref={mergedRowRef}
            gap={stacked ? 8 : 0}
            wrap={stacked ? 'wrap' : 'nowrap'}
            w="100%"
            className={clsx('rf-date-time-field', stacked && 'rf-date-time-field--stacked')}
        >
            {timeLabelledBy && (
                <span id={timeWordId} hidden>
                    время
                </span>
            )}
            <FieldA11yContext value={null}>
                <div style={stacked ? { flex: '1 1 100%', minWidth: 0 } : { flex: 1, minWidth: 0 }}>
                    <MaskedDateInput
                        value={date}
                        onChange={(d) => {
                            const newDate = d ?? null;
                            setParts((p) => ({ ...p, date: newDate }));
                            emit(newDate, time);
                        }}
                        minDate={minDate}
                        maxDate={maxDate}
                        excludeDate={isExcluded}
                        disabled={disabled}
                        readOnly={readOnly}
                        invalid={invalid}
                        clearable={clearable}
                        ref={ref}
                        aria-label={ariaLabel}
                        aria-labelledby={fieldA11y['aria-labelledby']}
                        aria-describedby={fieldA11y['aria-describedby']}
                        placeholder={placeholder ?? 'Дата'}
                        name={name}
                        popoverProps={{ position: 'bottom-start', ...popoverProps }}
                        data-testid={testid ? `datetime-field-date-${testid}` : undefined}
                    />
                </div>
                <MaskedTimeInput
                    value={time}
                    onChange={(t) => {
                        setParts((p) => ({ ...p, time: t }));
                        emit(date, t);
                    }}
                    withSeconds={withSeconds}
                    // Без даты время теряется (combine вернёт null) — дизейблим, чтобы не
                    // было «ввёл, а ничего не сохранилось».
                    disabled={disabled || !date}
                    readOnly={readOnly}
                    invalid={invalid}
                    clearable={clearable}
                    aria-label={timeLabel}
                    aria-labelledby={timeLabelledBy}
                    aria-describedby={fieldA11y['aria-describedby']}
                    popoverProps={popoverProps}
                    w={stacked ? '100%' : inlineTimeWidth}
                    data-testid={testid ? `datetime-field-time-${testid}` : undefined}
                />
            </FieldA11yContext>
        </Group>
    );
};

export default DateTimeField;
